import axios from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/v1';

export const REFRESH_TOKEN_KEY = 'aikka_refresh_token';

const safeStorage = {
  getItem(key: string): string | null {
    try { return localStorage.getItem(key); } catch { return null; }
  },
  setItem(key: string, value: string) {
    try { localStorage.setItem(key, value); } catch { /* noop */ }
  },
  removeItem(key: string) {
    try { localStorage.removeItem(key); } catch { /* noop */ }
  },
};

let authToken: string | null = safeStorage.getItem('aikka_token');

export function setAuthToken(token: string | null) {
  authToken = token;
  if (token) {
    safeStorage.setItem('aikka_token', token);
  } else {
    safeStorage.removeItem('aikka_token');
  }
}

export function setRefreshToken(token: string | null) {
  if (token) {
    safeStorage.setItem(REFRESH_TOKEN_KEY, token);
  } else {
    safeStorage.removeItem(REFRESH_TOKEN_KEY);
  }
}

export function clearAuthStorage() {
  authToken = null;
  safeStorage.removeItem('aikka_token');
  safeStorage.removeItem(REFRESH_TOKEN_KEY);
}

export function getAuthToken(): string | null {
  return authToken;
}

/** Le jeton de rafraîchissement encore en stock, s'il y en a un. */
export function getRefreshToken(): string | null {
  return safeStorage.getItem(REFRESH_TOKEN_KEY);
}

/** Default budget for a JSON round-trip. Uploads override it — see the interceptor. */
const DEFAULT_TIMEOUT_MS = 5000;

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  // NO default Content-Type on purpose. Setting one here applied it to EVERY
  // request, including the three that send a `FormData` (attach a file,
  // transcribe a clip, upload an avatar). A multipart body announced as JSON
  // carries no `boundary`, so the server's parser finds no parts and the route
  // answers "file is required" — a 400 on every upload in production, whatever
  // the file. Left unset, axios picks the type from the body it is given: JSON
  // for a plain object, and multipart with a correct boundary for a FormData.
  // The default was never doing any work; it was only ever breaking uploads.
  timeout: DEFAULT_TIMEOUT_MS,
  withCredentials: true, // Send cookies for SSO auth on .aikka.ai
});

/**
 * An upload needs room to finish.
 *
 * The 5s default is right for a JSON round-trip and wrong for a body carrying
 * megabytes: a document on a normal connection outlives it, so once the content
 * type is fixed the upload would simply die as a timeout instead of a 400. A
 * caller that set its own timeout keeps it.
 */
const UPLOAD_TIMEOUT_MS = 120_000;

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (authToken && config.headers) {
    config.headers.Authorization = `Bearer ${authToken}`;
  }
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (config.timeout === undefined || config.timeout === DEFAULT_TIMEOUT_MS) {
      config.timeout = UPLOAD_TIMEOUT_MS;
    }
  }
  return config;
});

function isAuthEndpoint(url: string | undefined): boolean {
  return (
    !!url &&
    (url.includes('/auth/login') ||
      url.includes('/auth/register') ||
      url.includes('/auth/refresh'))
  );
}

// Routes that require a session — a dead session there should return to /login.
// The agent app lives at /agent (not /dashboard), so the old /dashboard-only
// check left an expired agent session stranded instead of re-authing it.
const PROTECTED_ROUTE_PREFIXES = ['/dashboard', '/agent', '/projects', '/artefacts', '/spaces'];
function redirectToLoginIfProtected() {
  const path = window.location.pathname;
  if (PROTECTED_ROUTE_PREFIXES.some((p) => path.startsWith(p))) {
    window.location.pathname = '/login';
  }
}

// The access token lives 15 minutes; the refresh token lives 7 days. The old 401
// handler wiped storage and logged the user out the moment the access token
// expired — never spending the refresh token — so a session died every ~15 min.
// Now a 401 on a protected call refreshes the access token ONCE and replays the
// original request; only a failed refresh (absent/expired refresh token) ends
// the session. Concurrent 401s share one refresh via `refreshPromise`.
let refreshPromise: Promise<string> | null = null;

async function performRefresh(): Promise<string> {
  const refreshToken = safeStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) throw new Error('no refresh token');
  // Bare axios (not apiClient) so this call can never re-enter the 401 handler.
  const res = await axios.post<AuthTokensResponse>(
    `${API_BASE_URL}/auth/refresh`,
    { refreshToken },
    { withCredentials: true, timeout: 10000 },
  );
  setAuthToken(res.data.accessToken);
  setRefreshToken(res.data.refreshToken);
  return res.data.accessToken;
}

/** Refresh the access token, de-duplicating concurrent callers onto one request. */
function refreshAccessToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = performRefresh();
    // Reset the singleton once settled so a later expiry can refresh again.
    void refreshPromise.catch(() => undefined).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/** Bring fetch/SSE callers into the same session recovery as JSON requests. */
export async function refreshRejectedAuthToken(rejectedToken: string | null): Promise<string> {
  // A concurrent JSON request may already have refreshed while the rejected
  // stream was in flight. Reuse that token instead of rotating it again.
  const current = getAuthToken();
  if (current && current !== rejectedToken) return current;
  try {
    return await refreshAccessToken();
  } catch (error) {
    clearAuthStorage();
    redirectToLoginIfProtected();
    throw error;
  }
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;
    if (
      error.response?.status === 401 &&
      original &&
      !isAuthEndpoint(original.url) &&
      !original._retry
    ) {
      original._retry = true;
      try {
        const token = await refreshAccessToken();
        original.headers = original.headers ?? {};
        (original.headers as Record<string, string>).Authorization = `Bearer ${token}`;
        return apiClient(original);
      } catch {
        clearAuthStorage();
        redirectToLoginIfProtected();
      }
    }
    return Promise.reject(error);
  },
);

export { apiClient };

// ─── Auth API ─────────────────────────────────────────────
export interface AuthTokensResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

/** One live session, as returned by GET /auth/sessions.
 *  Device / location / last-used are captured at token issue/rotation
 *  (see docs/account-sessions-gap.md). Every one is nullable: a pre-tracking
 *  session — or a client that sent no headers — leaves them null, and the UI
 *  degrades to an honest fallback rather than inventing a device or place.
 *  `location` is a coarse "City, Country" (or "Country") geolocated server-side
 *  from the ANONYMISED /24 prefix — never a full IP, never GPS. `city`/`country`
 *  are the structured parts; `ipPrefix` is the raw coarse prefix kept only as a
 *  tooltip / fallback when geolocation misses. */
export interface AccountSession {
  id: string;
  createdAt: string;
  expiresAt: string;
  current: boolean;
  device: string | null;
  location: string | null;
  city: string | null;
  country: string | null;
  ipPrefix: string | null;
  lastUsedAt: string | null;
}

export interface AccountSessionsResponse {
  deviceTrackingSupported: boolean;
  sessions: AccountSession[];
}

/** One trusted device, as returned by GET /auth/trusted-devices. A trusted
 *  device is an OPAQUE cookie-backed recognition marker for one browser —
 *  DISTINCT from an active session (see docs/account-sessions-gap.md). `label`
 *  is parsed from the User-Agent at trust time and is null when unrecognised
 *  (the UI shows an honest placeholder, never a fabricated name). `current`
 *  flags the browser the caller is using right now. */
export interface TrustedDevice {
  id: string;
  label: string | null;
  createdAt: string;
  lastSeenAt: string;
  current: boolean;
}

export interface TrustedDevicesResponse {
  devices: TrustedDevice[];
  /** Whether the caller's current browser is already a trusted device. */
  currentDeviceTrusted: boolean;
}

export const authApi = {
  login: (email: string, password: string) =>
    apiClient.post<AuthTokensResponse>('/auth/login', { email, password }),
  register: (data: {
    email: string;
    password: string;
    name: string;
    accountName?: string;
    captchaToken?: string;
  }) =>
    apiClient.post<AuthTokensResponse>('/auth/register', data),
  /**
   * Termine la session CÔTÉ SERVEUR — la seule moitié qui manquait.
   *
   * `logout()` ne faisait que du ménage local : stockage vidé, cookie JS effacé,
   * cache React Query remis à zéro. Mais les cookies httpOnly posés par le back
   * restaient, et `JwtStrategy` lit le cookie d'accès en repli de l'en-tête :
   * le `GET /auth/me` du chargement suivant repartait authentifié, et la session
   * se rallumait toute seule.
   *
   * Le jeton voyage quand on l'a encore ; à défaut le serveur prend celui de son
   * propre cookie. Dans les deux cas il révoque et efface.
   */
  logout: (refreshToken: string | null) =>
    apiClient.post<{ message: string }>('/auth/logout', refreshToken ? { refreshToken } : {}),
  me: () => apiClient.get('/auth/me'),
  /**
   * Jeton signé prouvant l'identité de l'utilisateur au messager Intercom.
   *
   * `token: null` n'est pas une erreur : c'est la réponse quand le secret
   * Intercom n'est pas configuré côté serveur (dev local, ou back pas encore
   * déployé — Amplify livre le front AVANT que le back ne passe les E2E).
   * L'appelant boote alors le messager sans identité signée, comme avant.
   */
  intercomToken: () =>
    apiClient.get<{ token: string | null; expiresIn: number }>('/auth/intercom-token'),
  updateProfile: (data: { name: string }) => apiClient.patch('/auth/me', data),
  // Upload a profile avatar (multipart). Returns the refreshed profile with the
  // new avatarUrl. Same FormData pattern as agentApi.uploadFile / transcribe.
  uploadAvatar: (file: File) => {
    const form = new FormData();
    form.append('avatar', file, file.name);
    return apiClient.post('/auth/me/avatar', form);
  },
  resendVerification: () =>
    apiClient.post<{ message: string }>('/auth/resend-verification'),
  /**
   * Confirme une adresse e-mail depuis le lien reçu par courrier.
   *
   * `GET /auth/verify-email?token=` existait côté serveur depuis toujours ;
   * rien ne l'appelait, parce que le lien de l'e-mail pointait vers une route
   * de hash inexistante (voir `EmailService.sendVerificationEmail`). Public :
   * on clique ce lien depuis une boîte mail, souvent sur un autre appareil,
   * donc sans session.
   */
  verifyEmail: (token: string) =>
    apiClient.get<{ verified: boolean; email: string }>('/auth/verify-email', {
      params: { token },
    }),
  // `captchaToken` : jeton reCAPTCHA v3, absent tant que reCAPTCHA est inactif
  // (voir src/lib/recaptcha.ts). Un back qui ne le connaît pas encore l'ignore
  // (`whitelist: true`).
  forgotPassword: (email: string, captchaToken?: string) =>
    apiClient.post<{ message: string }>('/auth/forgot-password', { email, captchaToken }),
  resetPassword: (token: string, password: string, captchaToken?: string) =>
    apiClient.post<{ message: string }>('/auth/reset-password', { token, password, captchaToken }),
  // ─── Account management (Settings → Compte) ───────────────
  // The two destructive actions re-authenticate server-side: the current
  // password is re-entered and verified by the backend, so a stolen access
  // token alone can never trigger them (the UI confirmation is not enough).
  logoutAllDevices: (password: string) =>
    apiClient.post<{ message: string; revoked: number }>('/auth/logout-all-devices', {
      password,
    }),
  listSessions: () => apiClient.get<AccountSessionsResponse>('/auth/sessions'),
  // Revoking a session OTHER than the current one requires server-side re-auth
  // (the current password), so a stolen access token can't kick the user off
  // their other devices. The current session (hidden from this control in the
  // UI) needs no password.
  revokeSession: (id: string, password?: string) =>
    apiClient.post<{ revoked: boolean }>(
      `/auth/sessions/${id}/revoke`,
      password ? { password } : {},
    ),
  deleteAccount: (password: string) =>
    apiClient.delete<{ deleted: true }>('/auth/me', { data: { password } }),
  // ─── Trusted devices ("remember this device", claude.ai parity) ───────────
  // A trusted device is an opaque, cookie-backed recognition marker for one
  // browser, DISTINCT from an active session. `trustCurrentDevice` sets a
  // long-lived httpOnly cookie server-side; `removeTrustedDevice` deletes the
  // row (and clears that cookie when it's the current browser), truly revoking.
  listTrustedDevices: () =>
    apiClient.get<TrustedDevicesResponse>('/auth/trusted-devices'),
  trustCurrentDevice: () =>
    apiClient.post<{ device: TrustedDevice }>('/auth/trusted-devices/trust'),
  removeTrustedDevice: (id: string) =>
    apiClient.delete<{ removed: boolean }>(`/auth/trusted-devices/${id}`),
};

// ─── Billing API ──────────────────────────────────────────
import type {
  Subscription,
  Invoice,
  CreditBalance,
  PaymentMethodList,
  AutoRechargeSettings,
  AutoRechargeUpdate,
} from '@/types';

/** One sellable Aikka agent credit pack. */
export interface AgentCreditPack {
  key: string;
  credits: number;
  priceEurCents: number;
}

export interface AgentCreditPackList {
  currency: string;
  /**
   * The pack AIKKA-BETA-2026 applies to, so the UI can point the code at it —
   * `null` once this user has taken their one beta dotation. The beta is one per
   * person; the packs themselves stay buyable either way, so a null here hides the
   * code hint and nothing else.
   */
  betaPackKey: string | null;
  promotionCodeHint: string | null;
  packs: AgentCreditPack[];
}

export const billingApi = {
  checkout: (data: { productSlug: string; plan: string }) =>
    apiClient.post<{ url: string; sessionId: string }>('/billing/checkout', data),
  portal: () =>
    apiClient.post<{ url: string }>('/billing/portal'),
  subscriptions: () =>
    apiClient.get<Subscription[]>('/billing/subscriptions'),
  invoices: () =>
    apiClient.get<Invoice[]>('/billing/invoices'),
  credits: () =>
    apiClient.get<CreditBalance>('/billing/credits'),
  refill: (creditPack: number) =>
    apiClient.post<{ url: string; sessionId: string }>('/billing/credits/refill', { creditPack }),
  /**
   * Buy an Aikka AGENT credit pack — tops up `User.agentCredits`, the balance the
   * OS agent spends, unlike `refill` above which tops up the store ledger.
   *
   * The returned Checkout has Stripe's promotion-code field enabled, which is how
   * AIKKA-BETA-2026 is redeemed; `promotionCodeHint` echoes the campaign code so
   * the UI cannot drift from what Stripe actually accepts.
   */
  /**
   * The agent credit packs this deployment can sell. Derived server-side from the
   * same catalogue the Checkout bills against, so the UI never carries a price
   * string that could drift from Stripe.
   */
  agentCreditPacks: () =>
    apiClient.get<AgentCreditPackList>('/billing/credits/agent-packs'),
  buyAgentCreditPack: (packKey: string) =>
    apiClient.post<{
      url: string;
      sessionId: string;
      /** Null when this user has already redeemed the beta — the code is not offered again. */
      promotionCodeHint?: string | null;
      mock?: boolean;
    }>(
      '/billing/credits/agent-pack',
      { packKey },
    ),
  /** Every card on file plus which one would actually be billed. */
  paymentMethod: () => apiClient.get<PaymentMethodList>('/billing/payment-method'),
  /**
   * Hosted card capture (Stripe Checkout, mode `setup`). A FULL-PAGE navigation,
   * like every other Checkout here: the handler awaits a request before it has a
   * URL, so a popup opened afterwards would be blocked.
   */
  setupPaymentMethod: () =>
    apiClient.post<{ url: string; sessionId: string }>('/billing/payment-method/setup'),
  setDefaultPaymentMethod: (paymentMethodId: string) =>
    apiClient.put<PaymentMethodList>('/billing/payment-method/default', { paymentMethodId }),
  /** POST and not DELETE: the id travels in the body. */
  removePaymentMethod: (paymentMethodId: string) =>
    apiClient.post<PaymentMethodList>('/billing/payment-method/remove', { paymentMethodId }),
  autoRecharge: () => apiClient.get<AutoRechargeSettings>('/billing/auto-recharge'),
  updateAutoRecharge: (body: AutoRechargeUpdate) =>
    apiClient.put<AutoRechargeSettings>('/billing/auto-recharge', body),
};

// ─── Connecteurs ──────────────────────────────────────────

export const connectorsApi = {
  /**
   * « Demander des connecteurs » — prévient l'équipe par courriel.
   *
   * Le corps est vide : la demande ne porte que ce que l'utilisateur
   * authentifié est déjà connu pour être. `alreadyRequested` dit qu'une demande
   * a déjà été envoyée dans les 24 h et que la route a refusé d'en envoyer une
   * seconde — ce n'est PAS une erreur, et le panneau ne doit pas l'afficher
   * comme telle.
   */
  request: () =>
    apiClient.post<{
      requested: boolean;
      alreadyRequested: boolean;
      requestedAt: string | null;
    }>('/connectors/request'),
};

// ─── Chat API ─────────────────────────────────────────────
export const chatApi = {
  sendMessage: (data: { product: string; message: string; conversationId?: string }) =>
    apiClient.post<{ message: string; conversationId?: string }>('/chat/message', data),
};
